import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLt2FocusStore } from '@/stores/lt2FocusStore';
import type { Lt2CardTargets } from '@/stores/lt2FocusStore';

/**
 * How long a row's observer-stamped visibility stays usable. Past this the
 * listener measures again (the rect path). Kept well inside the listener's
 * 1000 ms freshness window by the re-observe nudge below.
 */
const REOBSERVE_MS = 500;
/** Safety-net rescan of the card root for rows the observers do not know. */
const RESCAN_MS = 1000;
/** Retry cadence when a focused card has painted no rows yet. */
const REDISCOVER_MS = 500;

const MARK = 'data-lt2-focused';
/**
 * Sniper v2 Phase 2 — the level-cursor rungs (`Lt2HotkeyListener`'s
 * `LEVEL_MARK` / `LEVEL_CARD_MARK`). They are a SEPARATE mark namespace from
 * `data-lt2-focused` on purpose (a twelve-rung ladder reusing it would make the
 * card-level price fence ambiguous and disarm A/D), but they are proved for
 * visibility by the same `cardIsVisible` path — so they want the same published
 * IntersectionObserver flags. Without this a level-addressed keypress pays a
 * forced style+layout flush on a board React re-renders every ≤33 ms, which is
 * the single largest pre-send cost Deploy C removed.
 */
const LEVEL_MARK = 'data-lt2-level';
const LEVEL_CARD_MARK = 'data-lt2-level-card';

function markSelectorFor(id: string): string {
  // Inside a quoted attribute value only the quote and the backslash need
  // escaping; card ids carry colons, which are fine there.
  return `[${MARK}="${id.replace(/["\\]/g, '\\$&')}"]`;
}

/**
 * The lowest element that is a PROPER ancestor of every row — the parent when
 * the rows are siblings (every current painter), higher when they are not.
 * Proper, so a row that joins later as a sibling (the ladder row an expanded
 * spread/total card paints beneath its action row) lands inside the root.
 */
function commonRootOf(rows: Element[]): Element | null {
  let root: Element | null = rows[0]?.parentElement ?? null;
  for (const row of rows) {
    while (root != null && !root.contains(row)) root = root.parentElement;
  }
  return root;
}

/**
 * Everything the FOCUSED card keeps alive so the keydown path can read flags
 * instead of measuring the page:
 *
 *   • its root (one document query, at the commit that painted the focus —
 *     click time, never keydown time), published to the store so the
 *     price-row lookup can scope its query;
 *   • an IntersectionObserver on each row it paints, publishing per-row
 *     {visible, atMs} by the SAME definition the rect path uses (CSS-visible
 *     and intersecting), stamped at the observer's own measurement time so a
 *     scroll that lands after the measurement always reads as newer;
 *   • a direct-child MutationObserver on the root, so a row that appears
 *     (ladder on expand) is enrolled before the next keydown, plus a 1 Hz
 *     rescan for anything deeper;
 *   • capture-phase scroll/resize listeners stamping the store's
 *     last-viewport-change, which invalidates every flag stamped before it.
 *
 * Fail-closed throughout: no IntersectionObserver (jsdom, old engines), no
 * rows, a root that left the document — each simply leaves no fresh flag,
 * and the listener falls back to the document/rect path it always took.
 */
class FocusedCardWatch {
  private readonly io: IntersectionObserver | null;
  private readonly mo: MutationObserver | null;
  private root: Element | null = null;
  private readonly observed = new Set<Element>();
  private lastRescanAt = 0;
  private lastReobserveAt = 0;
  private lastDiscoverAt = 0;

  constructor(private readonly id: string) {
    const store = useLt2FocusStore.getState();
    this.io =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                const target = entry.target;
                // The rect path refuses on an engine without checkVisibility;
                // publishing nothing here routes such an engine to that same
                // refusal instead of to a flag that says "visible".
                if (typeof target.checkVisibility !== 'function') continue;
                const cssVisible = target.checkVisibility({
                  checkOpacity: true,
                  checkVisibilityCSS: true,
                });
                // Strict, like the rect path: an edge-adjacent or zero-area
                // row (ratio 0) is not a row the trader can read a price from.
                const visible = cssVisible && entry.isIntersecting && entry.intersectionRatio > 0;
                store.noteRowVisibility(target, visible, measuredAtEpochMs(entry.time));
              }
            },
            { root: null, threshold: 0 },
          )
        : null;
    this.mo =
      typeof MutationObserver === 'function' ? new MutationObserver(() => this.sync(true)) : null;
    window.addEventListener('scroll', this.onViewportChange, { capture: true, passive: true });
    window.addEventListener('resize', this.onViewportChange, { capture: true, passive: true });
    this.discover();
  }

  private readonly onViewportChange = (): void => {
    useLt2FocusStore.getState().noteViewportChange(Date.now());
  };

  /** One document query, at focus time. Re-run only when the root has left
   *  the document or nothing was painted yet. */
  private discover(): void {
    this.lastDiscoverAt = Date.now();
    const rows = [...document.querySelectorAll(markSelectorFor(this.id))];
    const root = rows.length > 0 ? commonRootOf(rows) : null;
    this.mo?.disconnect();
    this.root = root;
    useLt2FocusStore.getState().setCardRoot(this.id, root);
    if (root != null) this.mo?.observe(root, { childList: true });
    this.sync(true);
  }

  /** Enrol the rows the card currently paints; drop the ones it no longer
   *  does. Scoped to the root, so it is cheap enough to run on a direct-child
   *  mutation and on the 1 Hz safety net. */
  private sync(force: boolean): void {
    const now = Date.now();
    if (!force && now - this.lastRescanAt < RESCAN_MS) return;
    this.lastRescanAt = now;
    const root = this.root;
    if (root == null || this.io == null) return;
    const current = new Set<Element>();
    // Both mark namespaces, each claiming its card by its own attribute. The
    // query stays scoped to the root discovered from the FOCUS marks, so a rung
    // painted outside that root simply gets no flag and the listener measures it
    // — the pre-Phase-2 path, never a flag that says "visible".
    for (const row of root.querySelectorAll(`[${MARK}],[${LEVEL_MARK}]`)) {
      const owner = row.getAttribute(MARK) ?? row.getAttribute(LEVEL_CARD_MARK);
      if (owner === this.id) current.add(row);
    }
    for (const row of this.observed) {
      if (!current.has(row)) {
        this.io.unobserve(row);
        this.observed.delete(row);
      }
    }
    for (const row of current) {
      if (!this.observed.has(row)) {
        this.io.observe(row);
        this.observed.add(row);
      }
    }
  }

  /** Per focused commit. O(1) unless something actually changed. */
  refresh(): void {
    const now = Date.now();
    if (this.root == null) {
      if (now - this.lastDiscoverAt >= REDISCOVER_MS) this.discover();
      return;
    }
    if (!this.root.isConnected) {
      this.discover();
      return;
    }
    this.sync(false);
    // Observers notify on CHANGE only, so a row that has sat on screen for
    // a while would carry an old stamp and send the listener back to the
    // rect path. Re-observing asks the engine for a fresh measurement on its
    // next rendering step — real geometry, no forced layout — so a rendering
    // board keeps its flags inside the freshness window.
    if (this.io != null && now - this.lastReobserveAt >= REOBSERVE_MS) {
      this.lastReobserveAt = now;
      for (const row of this.observed) {
        this.io.unobserve(row);
        this.io.observe(row);
      }
    }
  }

  stop(): void {
    window.removeEventListener('scroll', this.onViewportChange, { capture: true });
    window.removeEventListener('resize', this.onViewportChange, { capture: true });
    this.mo?.disconnect();
    this.io?.disconnect();
    this.observed.clear();
    useLt2FocusStore.getState().setCardRoot(this.id, null);
  }
}

/**
 * An observer entry's `time` is on the performance timeline; the store
 * compares stamps in epoch ms (Date.now), like every other lt2 stamp.
 */
function measuredAtEpochMs(entryTime: number): number {
  if (
    typeof performance !== 'undefined' &&
    Number.isFinite(performance.timeOrigin) &&
    Number.isFinite(entryTime) &&
    entryTime >= 0 &&
    entryTime <= performance.now()
  ) {
    return performance.timeOrigin + entryTime;
  }
  return Date.now();
}

/**
 * D-020 V2B — register a derivative card as a hotkey-focus target.
 *
 * The resolver ref is refreshed EVERY render, so `resolveFocused()` always
 * reads exactly what the card is currently displaying. Registration keys on
 * (id, enabled): a changed id (period/tab re-key) unregisters the old card —
 * clearing the focus if it was held — before registering the new one.
 *
 * Returns whether this card currently holds the focus (drives the ring).
 * Every `data-lt2-focused` painter derives its mark from this boolean in the
 * same render, which is what lets the hook tell the store what the card
 * PAINTED (`notePainted`) without reading the DOM back.
 */
export function useLt2Card(
  id: string | null,
  enabled: boolean,
  resolver: () => Lt2CardTargets | null,
): boolean {
  const resolverRef = useRef(resolver);
  const watchRef = useRef<FocusedCardWatch | null>(null);
  const focused = useLt2FocusStore((s) => enabled && id != null && s.focusedCardId === id);

  // Refresh before the browser can paint the new card values. This keeps the
  // registered callback stable without reading or mutating a ref during
  // render, while preserving the invariant that a visible hotkey target and
  // its resolver describe the same render.
  useLayoutEffect(() => {
    resolverRef.current = resolver;
    // The header target readout derives its label/Ready/Paused text from
    // resolveFocused(); re-registering the ref is invisible to subscribers,
    // so the FOCUSED card announces its re-render through the store's
    // presentation seq. Only the focused card bumps — the readout re-derives
    // exactly when its subject may have changed, and rows never subscribe to
    // the seq, so no render loop is possible.
    const store = useLt2FocusStore.getState();
    if (enabled && id != null && store.focusedCardId === id) store.notePresentation();
    watchRef.current?.refresh();
  });

  useEffect(() => {
    if (!enabled || id == null) return;
    return useLt2FocusStore.getState().register(id, () => resolverRef.current());
  }, [id, enabled]);

  // What this commit PAINTED. `focused` is the value the mark painters used
  // in this same render, so the store's ambiguity flag mirrors the DOM marks
  // without a query: between a focus change and its commit the old card is
  // still painted (and still reported), exactly as the DOM still shows it.
  useLayoutEffect(() => {
    if (id == null) return;
    const store = useLt2FocusStore.getState();
    store.notePainted(id, focused);
    return () => store.notePainted(id, false);
  }, [id, focused]);

  // The focused card — and only it — keeps the pre-send facts published.
  useLayoutEffect(() => {
    if (!focused || id == null) return;
    const watch = new FocusedCardWatch(id);
    watchRef.current = watch;
    return () => {
      watch.stop();
      if (watchRef.current === watch) watchRef.current = null;
    };
  }, [id, focused]);

  return focused;
}
